import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, Pattern, TicketViewModel } from '../../../models';
import { IDateNavigationService } from '../../../services';

class TicketSearchBarController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    searchCriteria: Pattern<TicketViewModel>[];
    searchString: string;

    static $inject = [
        '$q',
        '$stateParams',
        '$translate',
        'dateNavigationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$translate: angular.translate.ITranslateService,
        private _dateNavigationService: IDateNavigationService

    ) {
    }

    $onInit() {
        this.searchCriteria = [];
        this.getSearchCriteria()
            .then(data => this.searchCriteria = data)
            .then(() => this.preFilter());
    }

    private preFilter(): boolean {
        const preFilteredId = this._$stateParams['preFilteredId'];
        if (preFilteredId !== undefined) {
            this.searchString = `id:${preFilteredId}`;
            return true;
        }
        return false;
    }

    /**
     * Computes the search criteria. Can be extracted to a service.
     */
    private getSearchCriteria(): angular.IPromise<Pattern<TicketViewModel>[]> {
        const promises = {
            'today': this._dateNavigationService.getToday(),
            'thisweek': this._dateNavigationService.getThisWeek(),
            'thismonth': this._dateNavigationService.getThisMonth(),
            'lastweek': this._dateNavigationService.getLastWeek(),
            'lastmonth': this._dateNavigationService.getLastMonth()
        };
        return this._$q.all(promises)
            .then(data => {
                const searchCriteria: Pattern<TicketViewModel>[] = [
                    {
                        placeholder: 'Search pattern on ticket ID',
                        label: 'Ticket ID',
                        model: 'id',
                        property: 'id',
                        isMatch: (ticketViewmodel, reg, orig) => {
                            return ticketViewmodel.model.id === +orig;
                        }
                    },
                    {
                        placeholder: 'Search pattern on title',
                        label: 'Title',
                        model: 't',
                        property: 't',
                        isMatch: (ticketViewmodel, reg) => {
                            return reg.test(ticketViewmodel.model.title);
                        }
                    },
                    {
                        placeholder: 'Search pattern on user',
                        label: 'User',
                        model: 'u',
                        property: 'u',
                        isMatch: (ticketViewmodel, reg) => {
                            const isMatch =
                                reg.test(ticketViewmodel.assignedUser.displayName)
                                || reg.test(ticketViewmodel.closedUser.displayName)
                                || reg.test(ticketViewmodel.completedUser.displayName)
                                || reg.test(ticketViewmodel.creationUser.displayName)
                                || reg.test(ticketViewmodel.lastModifier.displayName);
                            return isMatch;
                        }
                    }, {
                        placeholder: 'Search on due date (e.g. thisweek, thismonth, etc.)',
                        label: 'Due date',
                        model: 'due',
                        property: 'due',
                        isMatch: (ticketViewmodel, reg, orig: string) => {
                            const ticketDueDate = moment(ticketViewmodel.model.revisedDueDate ?? ticketViewmodel.model.dueDate);
                            const dic: { [key: string]: string } = {};
                            Object.keys(data).forEach(key => {
                                dic[this._$translate.instant(key)] = key;
                            });
                            const now = this._$translate.instant('<now', null, null, null, null);
                            const res = data[dic[orig]];
                            let isMatch = false;
                            if (res) {
                                const { beginDateTime, endDateTime } = res;
                                isMatch = ticketDueDate.isAfter(moment(beginDateTime))
                                    && ticketDueDate.isBefore(moment(endDateTime));
                            } else if (orig === now) {
                                isMatch = ticketDueDate.isBefore(moment());
                            }
                            return isMatch;
                        }
                    }, {
                        placeholder: 'Search pattern on ticket category',
                        label: 'Category',
                        model: 'cat',
                        property: 'cat',
                        isMatch: (vm, reg) => vm.model.type && reg.test(vm.model.type?.name)
                    }

                ];
                return searchCriteria;
            });
    }
}

export const wtTicketSearchBar: ComponentDefinition = {
    componentId: 'wtTicketSearchBar',
    controller: TicketSearchBarController,
    template: require('./ticketSearchBar.component.html'),
    controllerAs: 'vm',
    bindings: {
        items: '<',
        filteredItems: '='
    }
};